import { useEffect, useMemo, useState } from "react";
import {
  extractReplayFrames,
  isCellLocked,
  liveReelGrid,
  waysCount,
  type ReplayFrame,
} from "../engine/replay";
import type { ReplayedOutcome } from "../engine/types";
import { GridPreview } from "./GridPreview";

/**
 * Spec 018 US5 (T043/T044) — spin-by-spin replay of one sampled
 * outcome, rendering the wild mechanics:
 *
 * - **Sticky** (T043): cells in the frame's locked mask render a
 *   distinct locked-cell indicator (lock glyph + accent ring) that
 *   persists across consecutive draws while the collection holds the
 *   position. Cells a `lock_position` op just added pulse as
 *   "newly locked".
 * - **Walking** (T044): a frame whose draw was preceded by a
 *   `shift_positions` op slides its locked cells in from the previous
 *   column (CSS `--walk-dx` keyed off `d_reel`) and shows a direction
 *   badge, so the wild visibly walks across the replay.
 * - **Expanding** (T044): reels an `expand_to_reel` op filled flash
 *   on the landing frame (single-spin effect — the next draw clears
 *   it, matching the engine's no-persistence semantics).
 *
 * Data source: `adapter.sampleOutcomes` (the CPU raw path), whose
 * `state_path` detail carries the per-draw replay payloads (FR-016).
 *
 * Spec 022 US3 (T032) — **Megaways**: when `megaways` is set (the plan
 * declares a per-reel `reel_height_distribution`), each reel renders at
 * its per-spin drawn height (the spec-020 dead-cell padding is trimmed
 * off, so short reels show short) and the per-spin ways count
 * (`∏ drawn-symbol populations`) is displayed. There is no paylines
 * overlay for a ways board, so nothing extra needs suppressing — the
 * grid renderer draws no lines.
 */
export function ReplayPanel({
  outcome,
  spinIndex,
  megaways = false,
}: {
  outcome: ReplayedOutcome;
  spinIndex: number;
  /**
   * The game is a Megaways game (per-spin variable reel heights). Trims
   * dead cells so reels render at their drawn height and shows the
   * per-spin ways count. Derived from the plan's
   * `reel_height_distributions` in `App`. Default `false` (fixed grid).
   */
  megaways?: boolean;
}) {
  const frames = useMemo(() => extractReplayFrames(outcome), [outcome]);
  const [frameIdx, setFrameIdx] = useState(0);
  useEffect(() => {
    setFrameIdx(0);
  }, [outcome]);

  const clamped = Math.min(frameIdx, frames.length - 1);
  const frame = frames[clamped];
  if (frame === undefined) return null;

  const hasWilds =
    frames.some(
      (f) =>
        f.lockedMask !== 0 ||
        f.newlyLocked.length > 0 ||
        f.expandedReels.length > 0 ||
        f.shift != null,
    ) || false;

  return (
    <section
      data-testid="replay-panel"
      className="border border-paper-border p-4 dark:border-term-border"
    >
      <header className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-sm font-bold uppercase tracking-widest text-paper-bright dark:text-term-bright">
            Spin replay
          </h2>
          <p className="text-[10px] text-paper-hint dark:text-term-hint">
            spin #{spinIndex.toLocaleString()} of the sample pass (same seed) ·{" "}
            {frames.length.toLocaleString()} draw
            {frames.length === 1 ? "" : "s"}
          </p>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <StepButton
            label="previous draw"
            disabled={clamped === 0}
            onClick={() => setFrameIdx(clamped - 1)}
          >
            ←
          </StepButton>
          <span
            data-testid="replay-frame-counter"
            className="tabular-nums text-paper-label dark:text-term-label"
          >
            draw {clamped + 1}/{frames.length}
          </span>
          <StepButton
            label="next draw"
            disabled={clamped >= frames.length - 1}
            onClick={() => setFrameIdx(clamped + 1)}
          >
            →
          </StepButton>
        </div>
      </header>

      <div className="mb-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[10px] text-paper-hint dark:text-term-hint">
        <span data-testid="replay-frame-state">
          state:{" "}
          <span className="text-paper-label dark:text-term-label">
            {frame.state}
          </span>
        </span>
        {frame.win != null && (
          <span>
            win:{" "}
            <span className="text-paper-label dark:text-term-label">
              {frame.win.toFixed(2)}x
            </span>
          </span>
        )}
        {frame.spinKind === "spin_respecting_locks" && (
          <span data-testid="replay-locks-respected">locks respected</span>
        )}
        {frame.shift != null && (
          <span
            data-testid="replay-walk-badge"
            className="text-cyan-700 dark:text-cyan-400"
          >
            walked {formatShift(frame.shift)}
          </span>
        )}
        {frame.expandedReels.length > 0 && (
          <span
            data-testid="replay-expand-badge"
            className="text-amber-700 dark:text-amber-400"
          >
            reel {frame.expandedReels.map((r) => r + 1).join(", ")} expanded
          </span>
        )}
        {megaways && (
          <span
            data-testid="replay-ways-count"
            className="text-violet-700 dark:text-violet-400"
          >
            ways:{" "}
            <span className="tabular-nums text-paper-label dark:text-term-label">
              {waysCount(frame.grid).toLocaleString()}
            </span>
          </span>
        )}
      </div>

      <ReplayGrid frame={frame} megaways={megaways} />

      {hasWilds && (
        <p className="mt-2 text-[10px] text-paper-hint dark:text-term-hint">
          🔒 locked (sticky) · ✦ newly locked · flashing reel = expanding wild ·
          slide = walking wild
        </p>
      )}
    </section>
  );
}

function ReplayGrid({
  frame,
  megaways,
}: {
  frame: ReplayFrame;
  megaways: boolean;
}) {
  // Spec 020 US5 — geometry is read from the frame's grid (reel-major,
  // self-describing: a 3×3 frame renders 3×3, a pyramid tapers), not a
  // hardcoded 5×3. `GridPreview` lays out the reel-columns and centers
  // short reels; each cell keeps its full wild-mechanic decoration via
  // `renderCell`.
  //
  // Spec 022 US3 — for a Megaways board, trim the spec-020 dead-cell
  // padding so each reel renders at its per-spin drawn height (a 2-tall
  // reel shows two cells against its 7-tall neighbours). `liveReelGrid`
  // is a no-op on a fixed grid (no dead cells), so a lines/fixed-ways
  // board renders exactly as before.
  const displayGrid = megaways ? liveReelGrid(frame.grid) : frame.grid;
  return (
    <GridPreview
      grid={displayGrid}
      testId="replay-grid"
      renderCell={(reel, row) => (
        <ReplayCell
          frame={frame}
          symbol={displayGrid[reel]?.[row] ?? ""}
          reel={reel}
          row={row}
        />
      )}
    />
  );
}

function ReplayCell({
  frame,
  symbol,
  reel,
  row,
}: {
  frame: ReplayFrame;
  symbol: string;
  reel: number;
  row: number;
}) {
  const locked = isCellLocked(frame.lockedMask, reel, row);
  const newlyLocked = frame.newlyLocked.some(
    ([r, w]) => r === reel && w === row,
  );
  const expanded = frame.expandedReels.includes(reel);
  const walked = frame.shift != null && locked;

  const classes = [
    "relative flex h-12 items-center justify-center border text-xs font-bold",
    locked || newlyLocked
      ? "border-2 border-amber-600 bg-amber-500/15 text-amber-800 dark:border-amber-400 dark:text-amber-300"
      : "border-paper-border text-paper-text dark:border-term-border dark:text-term-text",
    newlyLocked ? "animate-pulse" : "",
    expanded ? "replay-expand-flash" : "",
    walked ? "replay-walk-slide" : "",
  ]
    .filter(Boolean)
    .join(" ");

  // Walking direction: the indicator slides in from where the wild
  // stood on the previous draw (opposite of the applied shift).
  const style =
    walked && frame.shift != null
      ? ({ "--walk-dx": `${-frame.shift.dReel * 110}%` } as React.CSSProperties)
      : undefined;

  return (
    <div
      data-testid={`replay-cell-${reel}-${row}`}
      data-locked={locked || newlyLocked ? "true" : undefined}
      data-newly-locked={newlyLocked ? "true" : undefined}
      data-expanded={expanded ? "true" : undefined}
      data-walked={walked ? "true" : undefined}
      className={classes}
      style={style}
    >
      {(locked || newlyLocked) && (
        <span aria-hidden className="absolute left-0.5 top-0.5 text-[9px]">
          {newlyLocked ? "✦" : "🔒"}
        </span>
      )}
      <span className="truncate px-1">{symbol}</span>
    </div>
  );
}

function StepButton({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className="border border-paper-border px-2 py-0.5 text-paper-text hover:bg-paper-panel disabled:cursor-not-allowed disabled:opacity-40 dark:border-term-border dark:text-term-text dark:hover:bg-term-panel"
    >
      {children}
    </button>
  );
}

function formatShift(shift: { dReel: number; dRow: number }): string {
  const parts = [];
  if (shift.dReel !== 0)
    parts.push(`${shift.dReel < 0 ? "←" : "→"} ${Math.abs(shift.dReel)} reel`);
  if (shift.dRow !== 0)
    parts.push(`${shift.dRow < 0 ? "↑" : "↓"} ${Math.abs(shift.dRow)} row`);
  return parts.length > 0 ? parts.join(" · ") : "in place";
}
